/**
 * Finding and loading a module from the registry (logbook decisions 15 and 21).
 *
 * reserve.parkeze.com is a shell: its /reserve and /kiosk pages are the
 * `reservation-app` and `reservation-desk` modules, mounted from
 * modules.parkeze.com exactly as the operator console and the enforcement app
 * mount them. Nothing here knows what either module does.
 *
 *   registry.json  ->  modules[id].majors[major] = { version, entry, module_url }
 *   module_url     ->  <script type="module">, which defines `element`
 *
 * A module runs with the signed-in user's token, so the shell loads one only
 * from the registry's origin and only the element the registry names; a
 * module or major the registry does not serve is an error to show, not a URL
 * to guess.
 *
 * Local development (`npm run dev:modules`): the dev server serves the local
 * embed build as a registry at /local-modules (vite.config.ts), and the shell
 * reads that instead. Only a dev server honors it; a production build always
 * reads modules.parkeze.com.
 */

const LOCAL = import.meta.env.DEV && import.meta.env.VITE_LOCAL_MODULES === '1'

/** Where modules come from. */
export const MODULES_ORIGIN = LOCAL
  ? `${window.location.origin}/local-modules`
  : 'https://modules.parkeze.com'

export interface ResolvedModule {
  id: string
  major: number
  version: string
  element: string
  moduleUrl: string
}

interface RegistryRecord {
  id: string
  element: string
  surfaces?: string[]
  majors: Record<string, { version: string; entry: string; module_url: string }>
}

/** A valid custom element name, as the enforcement app checks it. */
const ELEMENT = /^[a-z][a-z0-9._]*-[a-z0-9._-]*$/

let registry: Promise<RegistryRecord[]> | null = null

function fetchRegistry(): Promise<RegistryRecord[]> {
  registry ??= fetch(`${MODULES_ORIGIN}/registry.json`, { cache: 'no-cache' })
    .then(async (res) => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return ((await res.json()) as { modules: RegistryRecord[] }).modules
    })
    .catch((e: unknown) => {
      // Not cached: Try again asks again.
      registry = null
      throw new Error(`The module registry could not be reached (${e instanceof Error ? e.message : String(e)}).`)
    })
  return registry
}

/** The URL and element the registry serves for `id` at `major`, or why not. */
export async function resolveModule(id: string, major: number): Promise<ResolvedModule> {
  const record = (await fetchRegistry()).find((m) => m.id === id)
  if (!record) throw new Error(`The registry does not serve ${id}.`)
  const served = record.majors?.[String(major)]
  if (!served) throw new Error(`The registry does not serve ${id} v${major}.`)
  if (!ELEMENT.test(record.element ?? '')) throw new Error(`The registry names an invalid element for ${id}.`)
  // The registry names the URL, but a module only ever loads from the
  // registry's own origin, under its own id.
  if (!served.module_url?.startsWith(`${MODULES_ORIGIN}/${id}/`)) {
    throw new Error(`${id} v${major} is not served from ${MODULES_ORIGIN}.`)
  }
  return { id, major, version: served.version, element: record.element, moduleUrl: served.module_url }
}

const scripts = new Map<string, Promise<void>>()
let failures = 0

/**
 * Injects the module's script once per page and waits for its element.
 *
 * A browser remembers a module URL that failed to load for the life of the
 * page, so after a failure the next attempt asks for the same file under a
 * new query string.
 */
export async function loadModule(module: ResolvedModule): Promise<void> {
  let loading = scripts.get(module.moduleUrl)
  if (!loading) {
    loading = new Promise<void>((resolve, reject) => {
      const script = document.createElement('script')
      script.type = 'module'
      script.src = failures ? `${module.moduleUrl}?attempt=${failures}` : module.moduleUrl
      script.onload = () => resolve()
      script.onerror = () => {
        script.remove()
        reject(new Error(`${module.id} ${module.version} could not be loaded from ${MODULES_ORIGIN}.`))
      }
      document.head.appendChild(script)
    }).catch((e: unknown) => {
      scripts.delete(module.moduleUrl)
      failures += 1
      throw e
    })
    scripts.set(module.moduleUrl, loading)
  }
  await loading
  // A module script's load fires after it has run, so a module that defines
  // the element the registry names has done so by now.
  if (!customElements.get(module.element)) {
    throw new Error(`${module.id} ${module.version} loaded but did not define <${module.element}>.`)
  }
}
